import { cn } from '@/lib/cn'
import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'
import { type ReactNode, useEffect } from 'react'
import { createPortal } from 'react-dom'

interface Props {
  open: boolean
  onClose: () => void
  title?: string
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  className?: string
}

// Sheet is a right-hand slide-over — same overlay/portal/Escape
// conventions as Dialog, but non-blocking in intent: callers close it
// freely without losing data (autosave, not a form to submit).
export function Sheet({ open, onClose, title, description, children, footer, className }: Props) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  if (typeof document === 'undefined') return null

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
          onClick={onClose}
          // biome-ignore lint/a11y/useSemanticElements: <dialog> lacks the motion primitives we need; aria-modal covers the semantics
          role="dialog"
          aria-modal="true"
        >
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.2 }}
            className={cn(
              'absolute inset-y-0 right-0 flex w-full max-w-md flex-col border-l border-border bg-background shadow-xl',
              className,
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {title ? (
              <div className="flex items-start justify-between border-b border-border px-6 py-4">
                <div className="flex flex-col gap-1">
                  <h2 className="text-lg font-semibold text-foreground">{title}</h2>
                  {description ? (
                    <div className="text-sm text-muted-foreground">{description}</div>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : null}
            <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
            {footer ? (
              <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-3">
                {footer}
              </div>
            ) : null}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  )
}
